/*
 * parseTwin
 *
 */

import { AdminTwin, Entity, LogEntry, ParsedTwin, TwinEntityType } from '@repo/frontend-types';
import { parseLatLong, parseOrientation } from '@repo/ui';
import { caseInsensitiveArrayIncludes } from '@repo/front-end-utils';

// Utils
import { stringToTwinEntityType } from '@repo/front-end-utils';
import { isValidModelFilename } from '@repo/ui';

type ModelResult = {
  physicalNode: Entity | null;
  filterNode: Entity | null;
};

/**
 * The entity's model file, screened against the known-model list: an unset value passes through as
 * it was, one the list does not recognise becomes `null` (the machine draws as the placeholder),
 * and a recognised one passes through unchanged.
 */
const validatedModelFile = (entity: Entity): string | null | undefined => {
  if (!entity.modelFile) return entity.modelFile;
  return isValidModelFilename(entity.modelFile) ? entity.modelFile : null;
};

export const parseTwin = ({
  organisation,
  twinEntity,
  adminTwin,
  physicalEntities,
  filterEntities,
  apolloExplorerURI,
}: {
  organisation: string;
  twinEntity: Entity;
  adminTwin: AdminTwin;
  physicalEntities: string[];
  filterEntities: string[];
  apolloExplorerURI?: string;
}): {
  twin: ParsedTwin;
  preFlightReport: LogEntry[];
  multifloor: boolean;
  hasFloorLevel: boolean;
} | void => {
  let preFlightReport: LogEntry[] = [];

  /*
   * Two questions, which one flag used to answer (`complex-measures-in-configuration-floor-placement`).
   * Since Go Live writes a FLOOR for a one-floor plan too (maria's ruling, 2026-10-03), a twin can
   * have a floor level with a single floor on it:
   *
   * - `hasFloorLevel` — the site's children are FLOORs (its first child is one, today's test). The
   *   STRUCTURAL answer: a top-level area's parent is a floor (`topLevelTypeFor`).
   * - `multifloor` — two or more FLOORs. The PRESENTATION answer: a Floor filter, a heat-map plane
   *   per floor. A LONE floor is not multi-floor, so a single-floor twin looks exactly as it did.
   */
  const isSite = stringToTwinEntityType(twinEntity.type.name) === TwinEntityType.SITE;
  const siteChildren = isSite ? (twinEntity.children ?? []) : [];
  const hasFloorLevel =
    siteChildren.length > 0 &&
    stringToTwinEntityType(siteChildren[0].type.name) === TwinEntityType.FLOOR;
  const floorCount = siteChildren.filter(
    child => stringToTwinEntityType(child.type.name) === TwinEntityType.FLOOR,
  ).length;
  const multifloor = hasFloorLevel && floorCount >= 2;

  // Recursive helper function to build models based on entity type
  const processEntity = (entity: Entity, currentDepth: number): ModelResult => {
    // Initialise objects for possible inclusion in physical and filter models
    let physicalNode: Entity | null = null;
    let filterNode: Entity | null = null;

    // Check if the current entity matches any type in physicalEntities
    if (caseInsensitiveArrayIncludes(physicalEntities, entity.type?.name)) {
      // Copy the whole entity for the physical model, its model file screened
      physicalNode = {
        ...entity,
        children: [],
        depth: currentDepth,
        modelFile: validatedModelFile(entity),
      };
    }

    // Check if the current entity matches any type in filterEntities
    if (caseInsensitiveArrayIncludes(filterEntities, entity.type?.name)) {
      // Copy the whole entity for the filter model, its model file screened
      filterNode = {
        ...entity,
        children: [],
        depth: currentDepth,
        modelFile: validatedModelFile(entity),
      };
    }

    // Process children recursively if they exist
    if (Array.isArray(entity.children) && entity.children.length > 0) {
      entity.children.forEach(child => {
        const { physicalNode: childPhysical, filterNode: childFilter } = processEntity(
          child,
          currentDepth + 1,
        );

        // Add child to physical model if it matches physicalEntities
        if (childPhysical && physicalNode) {
          physicalNode.children!.push(childPhysical);
        }

        // Add child to filter model if it matches filterEntities
        if (childFilter && filterNode) {
          filterNode.children!.push(childFilter);
        }
      });
    }

    // Return the nodes, whether populated or null
    return { physicalNode, filterNode };
  };

  // Start processing from the root
  const { physicalNode, filterNode } = processEntity(twinEntity, 0);

  if (physicalNode) {
    // Super-hack - copy the position and orientation from the admin twin
    // into the SITE in the physical model
    // as admin-db is now the source of truth
    physicalNode.rotationY = parseOrientation(adminTwin.orientation) ?? 0;
    physicalNode.mapOrigin = parseLatLong(adminTwin);

    const twin = {
      physicalModel: physicalNode,
      adminTwin,
      filterModel: filterNode,
      totalCapacity: physicalNode && physicalNode.capacity ? physicalNode.capacity : 0,
    };

    return {
      twin,
      preFlightReport,
      multifloor,
      hasFloorLevel,
    };
  } else {
    console.warn('Unable to parse Twin');
  }
};
